/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SWMLinkPage.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ComponentLinkTemplate from "./ComponentLinkTemplate";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import CustomToolTip from "../../../infoButton/CustomToolTip";
import CustomSearchbarWithApiV2 from "../../common/searchBar/CustomSearchbarWithApiV2";
import { componentStyle } from "../../../../config/appTheme";
import CustomSelectV2 from "../../common/select/CustomSelectV2";
import CustomAutoCompleteWithAPIV2 from "../../common/searchBar/CustomAutoCompleteWithAPIV2";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import CustomDialogBoxV2 from "../../common/customDialogBox/CustomDialogBoxV2";
import {
  labCar,
  HandleApiActions,
  adminSettingsAPI,
} from "../../../../redux/actions";
import softwareIntersectionAPI from "../../../../redux/actions/softwareIntersectionActions";
import customConsole from "../../../../config/customConsole";
import { ENTITY } from "../../SmartMatchingDialogContainer/constants/entityTypes";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import AISuggestionDialogContainer from "../../SmartMatchingDialogContainer/AiSuggestionDialogContainer";
import RecreateLinkDialog from "./RecreateLinkDialog";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModuleViewMoreDialog from "../../dashboard/pages/tickets/ModuleViewMoreDialog";

function SWMLinkPage({ onBeforeSmartMatch }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const zoneFromurl = queryParams.get("selectedZone");
  const eeFromurl = queryParams.get("ee_id");
  const swmFromurl = queryParams.get("swm_id");

  const { networkInterfacesSWM } = useSelector(
    (state) => state.networkInterfacesReducer,
  );

  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedSingleVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  // State for project/variant
  const [projectID, setProjectID] = useState(null);
  const [variantID, setVariantID] = useState(null);
  const [selectedProjectIsZonal, setSelectedProjectIsZonal] = useState(false);
  const [selectedProjectZonesList, setSelectedProjectZonesList] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Zone states - independent for left and right
  const [zoneOptionsList, setZoneOptionsList] = useState([]);
  const [selectedZoneMain, setSelectedZoneMain] = useState(null);
  const [selectedZoneLinked, setSelectedZoneLinked] = useState(null);

  // EE Component states
  const [eeComponentsMainList, setEEComponentsMainList] = useState([]);
  const [selectedEEMainId, setSelectedEEMainId] = useState(null);
  const [selectedEEComponentMain, setSelectedEEComponentMain] = useState({});

  const [eeComponentsLinkedList, setEEComponentsLinkedList] = useState([]);
  const [selectedEELinkedId, setSelectedEELinkedId] = useState(null);
  const [selectedEEComponentLinked, setSelectedEEComponentLinked] = useState(
    {},
  );

  // SWM states
  const [SWMComponentMainList, setSWMComponentMainList] = useState([]);
  const [SWMComponentsLinkedList, setSWMComponentsLinkedList] = useState([]);
  const [selectedMainSWMDetails, setSelectedMainSWMDetails] = useState(null);

  // Fetch project/variant info
  const [leftPage, setLeftPage] = useState(1);
  const [leftRowsPerPage] = useState(25);
  const [rightPage, setRightPage] = useState(1);
  const [rightRowsPerPage] = useState(25);

  const [swmMainCount, setSwmMainCount] = useState(0);
  const [swmLinkCount, setSwmLinkCount] = useState(0);

  // UI states
  // Counter to track latest EE fetch requests and ignore stale responses
  const eeRequestCounter = useRef(0);
  // Counter to track latest SWM fetch requests and ignore stale responses
  const swmRequestCounter = useRef(0);
  // Debounce timers for zone changes
  const leftZoneTimerRef = useRef(null);
  const rightZoneTimerRef = useRef(null);
  // Remember selected left SWM id to preserve selection across refreshes
  const selectedMainSWMIdRef = useRef(null);

  const [modifiedSWMComponentList, setModifiedSWMComponentList] = useState([]);
  const [modifiedSWMComponentLinkList, setModifiedSWMComponentLinkList] =
    useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);
  const [pendingSWMActionDialogOpen, setPendingSWMActionDialogOpen] =
    useState(false);
  const [selectedPendingSWMAction, setSelectedPendingSWMAction] =
    useState(null);
  const [selectedNetworkInterface, setSelectedNetworkInterface] =
    useState(null);
  const [selectedNetworkItem, setSelectedNetworkItem] = useState(null);
  const [networkDialogOpen, setNetworkDialogOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [linkApiProps, setLinkApiProps] = useState(null);
  const [apiAction, setApiAction] = useState("");
  const [apiDataKey, setApiDataKey] = useState("");
  const [moduleViewMoreType, setModuleViewMoreType] = useState("");
  const [isSWMDialogOpen, setIsSWMDialogOpen] = useState(false);

  /* Smart matching states */
  const [openAISuggestion, setOpenAISuggestion] = useState(false);
  const [showSmartMatchDialog, setshowSmartMatchDialog] = useState(false);
  const [componentToBeMatched, setComponentToBeMatched] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);

  // recreate states
  const [openSWMLinkRecreateDialog, setOpenSWMLinkRecreateDialog] =
    useState(false);
  const [recreateSWMLinkList, setRecreateSWMLinkList] = useState([]);
  const [selectedRecreateSWMForLink, setSelectedRecreateSWMForLink] =
    useState(null);

  const handleSmartMatchRequest = async (componentDetails) => {

    const componentName = componentDetails?.description;
    if (componentName?.length > 0) {
      setComponentToBeMatched([componentName]);
    }
    setshowSmartMatchDialog(true);
    setOpenAISuggestion(true);
    setSelectedEntity({
      source: [
        {
          entityType: ENTITY.TYPE.SWM,
          entityId: componentDetails?.swm_id,
        },
      ],
      targetEntityTypes: [ENTITY.TYPE.SWM],
      topK: 5,
      minConfidence: 0.1,
      description: componentName,
    });
  };
  // Fetch project/variant info
  useEffect(() => {
    setProjectID(selectedProjectDetails?.project_id);
    setVariantID(selectedSingleVariantDetails?.variant_id);
    setSelectedProjectIsZonal(selectedProjectDetails?.is_zonal);
    setSelectedProjectZonesList(selectedProjectDetails?.project_zones);
    setIsInitialized(false); // Reset initialized flag when project changes
    setSelectedZoneMain(null);
    setSelectedZoneLinked(null);
    setZoneOptionsList([]);

    customConsole({
      title: "🔄 PROJECT CHANGED",
      projectId: selectedProjectDetails?.project_id,
      variantId: selectedSingleVariantDetails?.variant_id,
      isZonal: selectedProjectDetails?.is_zonal,
      zonesCount: selectedProjectDetails?.project_zones?.length,
    });
  }, [selectedProjectDetails, selectedSingleVariantDetails]);

  useEffect(() => {
    if (!eeFromurl) return;

    if (String(selectedEEMainId) !== String(eeFromurl)) {
      setSelectedEEMainId(Number(eeFromurl));
    }
  }, [eeFromurl, selectedEEMainId]);

  // Fetch network interfaces
  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_swm_network_interfaces,
        params: {
          network_type: "swm",
        },
        show_toast: false,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (networkInterfacesSWM) {
    }
  }, [networkInterfacesSWM]);

  useEffect(() => {
    if (networkDialogOpen && !openSWMLinkRecreateDialog) {
      setSelectedNetworkInterface(null);
    }
  }, [networkDialogOpen]);

  /* ===== API Dispatch Functions ===== */

  const getEEMainComponentsList = useCallback(
    async ({ projectId, zonePosition, variantId }) => {
      let apiDetails = labCar.get_ee_components_new;
      // tag this request
      const reqId = ++eeRequestCounter.current;

      customConsole({
        ...apiDetails,
        params: {
          status: true,
          project_id: projectId,
          zone_position: zonePosition,
          variant_id: variantId,
        },
      });

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            status: true,
            project_id: projectId,
            zone_position: zonePosition,
            variant_id: variantId,
          },
          show_toast: false,
        }),
      );

      // ignore stale responses
      if (reqId !== eeRequestCounter.current) return resp;

      if (resp.error) {
        setEEComponentsMainList([]);
      } else {
        setEEComponentsMainList(resp.data);
      }
      return resp;
    },
    [dispatch],
  );

  const getEELinkedComponentsList = useCallback(
    async ({ projectId, zonePosition, variantId }) => {
      let apiDetails = labCar.get_ee_components_new;
      const reqId = ++eeRequestCounter.current;

      customConsole({
        ...apiDetails,
        params: {
          status: true,
          project_id: projectId,
          zone_position: zonePosition,
          variant_id: variantId,
        },
      });

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            status: true,
            project_id: projectId,
            zone_position: zonePosition,
            variant_id: variantId,
          },
          show_toast: false,
        }),
      );

      // ignore stale responses
      if (reqId !== eeRequestCounter.current) return resp;

      if (resp.error) {
        setEEComponentsLinkedList([]);
      } else {
        setEEComponentsLinkedList(resp.data);
      }
      return resp;
    },
    [dispatch],
  );

  const getEEMainLinkedComponentsList = useCallback(
    async ({ projectId, variantId }) => {
      let apiDetails = labCar.get_ee_components_new;
      const reqId = ++eeRequestCounter.current;

      customConsole({
        ...apiDetails,
        params: {
          status: true,
          project_id: projectId,
          variant_id: variantId,
        },
      });

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            status: true,
            project_id: projectId,
            variant_id: variantId,
          },
          show_toast: false,
        }),
      );

      // ignore stale responses
      if (reqId !== eeRequestCounter.current) return resp;

      if (resp.error) {
        setEEComponentsMainList([]);
        setEEComponentsLinkedList([]);
      } else {
        setEEComponentsMainList(resp.data);
        setEEComponentsLinkedList(resp.data);
      }
      return resp;
    },
    [dispatch],
  );

  const getMainSWModulesList = useCallback(
    async ({
      projectId,
      eeId,
      variantId,
      zonePosition,
      pageNo = 1,
      pageSize = leftRowsPerPage,
    }) => {
      let apiDetails = labCar.get_sw_modules_new;
      const reqId = ++swmRequestCounter.current;

      customConsole({
        ...apiDetails,
        params: {
          project_id: projectId,
          ee_id: eeId,
          variant_id: variantId,
          ...(swmFromurl ? { swm_id: Number(swmFromurl) } : {}),
          status: true,
          ...(zonePosition ? { zone_position: zonePosition } : {}),
          page_number: pageNo,
          page_size: pageSize,
        },
      });

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            status: true,
            project_id: projectId,
            variant_id: variantId,
            ee_id: eeId,
            ...(swmFromurl ? { swm_id: Number(swmFromurl) } : {}),
            ...(zonePosition ? { zone_position: zonePosition } : {}),
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      );

      // ignore stale responses
      if (reqId !== swmRequestCounter.current) return resp;

      if (resp.error) {
        setSWMComponentMainList([]);
        setSelectedMainSWMDetails(null);
        setSwmMainCount(0);
      } else {
        setSWMComponentMainList(resp.data);
        setSwmMainCount(resp.total_count || resp.data?.length || 0);
        // Prefer previously selected SWM if it still exists; else URL param; else first
        const currentSelectedId = selectedMainSWMIdRef.current;
        let selectedToSet = null;
        if (currentSelectedId) {
          selectedToSet = resp.data?.find(
            (item) => String(item?.swm_id) === String(currentSelectedId),
          );
        }
        if (!selectedToSet && resp.data?.length > 0) {
          selectedToSet = swmFromurl
            ? resp.data.find(
              (item) => String(item?.swm_id) === String(swmFromurl),
            )
            : resp.data[0];
        }
        setSelectedMainSWMDetails(selectedToSet || null);
      }
      return resp;
    },
    [dispatch, swmFromurl, leftRowsPerPage],
  );

  const getLinkedSWModulesList = useCallback(
    async ({
      projectId,
      eeId,
      variantId,
      zonePosition,
      pageNo = 1,
      pageSize = rightRowsPerPage,
    }) => {
      let apiDetails = labCar.get_sw_modules_new;

      customConsole({
        ...apiDetails,
        params: {
          status: true,
          project_id: projectId,
          ee_id: eeId,
          ...(zonePosition ? { zone_position: zonePosition } : {}),
          page_number: pageNo,
          page_size: pageSize,
        },
      });

      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            status: true,
            project_id: projectId,
            variant_id: variantId,
            ee_id: eeId,
            ...(zonePosition ? { zone_position: zonePosition } : {}),
            page_number: pageNo,
            page_size: pageSize,
          },
          show_toast: false,
        }),
      );

      if (resp.error) {
        setSWMComponentsLinkedList([]);
        setSwmLinkCount(0);
      } else {
        setSWMComponentsLinkedList(resp.data);
        setSwmLinkCount(resp.total_count || resp.data?.length || 0);
      }
    },
    [dispatch, rightRowsPerPage],
  );

  // keep ref in sync with selection state
  useEffect(() => {
    selectedMainSWMIdRef.current = selectedMainSWMDetails?.swm_id || null;
  }, [selectedMainSWMDetails]);

  const getSWModulesIntersectionsList = useCallback(
    async ({ projectId, eeId, swmId, variantId }) => {
      if (projectId && eeId) {
        let apiDetails = softwareIntersectionAPI.get_software_intersections;

        customConsole({
          ...apiDetails,
          params: {
            status: true,
            project_id: Number(projectId),
            variant_id: variantId,
            ee_id: Number(eeId),
            ...(swmId && { swm_id: Number(swmId) }),
          },
        });

        const resp = await dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              status: true,
              project_id: Number(projectId),
              variant_id: variantId,
              ee_id: Number(eeId),
              ...(swmId && { swm_id: Number(swmId) }),
            },
            show_toast: false,
          }),
        );

        if (resp.error) {
          // Handle error
        } else {
          // Data is in resp.data
        }
      }
    },
    [dispatch],
  );

  const handleSWModuleLink = useCallback(
    (selectedLinkedSWModuleDetails) => {
      if (
        projectID &&
        selectedSingleVariantDetails &&
        selectedSingleVariantDetails?.variant_id &&
        selectedMainSWMDetails &&
        selectedMainSWMDetails?.ee_id &&
        selectedMainSWMDetails?.swm_id &&
        selectedLinkedSWModuleDetails &&
        selectedLinkedSWModuleDetails?.ee_id &&
        selectedLinkedSWModuleDetails?.swm_id
      ) {
        let currentTime = new Date().toISOString();

        dispatch(
          HandleApiActions({
            ...labCar.link_sw_module,
            params: {
              project_id: Number(projectID),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              parent: {
                ee_id: Number(selectedMainSWMDetails?.ee_id),
                swm_id: Number(selectedMainSWMDetails?.swm_id),
              },
              child: {
                ee_id: Number(selectedLinkedSWModuleDetails?.ee_id),
                swm_id: Number(selectedLinkedSWModuleDetails?.swm_id),
              },
              current_time: currentTime,
              network_interface: selectedNetworkInterface,
              module_type: "swm",
            },
            show_toast: true,
          }),
        ).then(() => {
          if (projectID && selectedEEMainId) {
            getMainSWModulesList({
              projectId: projectID,
              eeId: selectedEEMainId,
              variantId: selectedSingleVariantDetails?.variant_id,
              zonePosition: selectedProjectIsZonal
                ? selectedZoneMain
                : undefined,
              pageNo: leftPage,
              pageSize: leftRowsPerPage,
            });
          }
          if (
            projectID &&
            selectedEELinkedId &&
            selectedSingleVariantDetails?.variant_id &&
            selectedSingleVariantDetails
          ) {
            getLinkedSWModulesList({
              projectId: projectID,
              eeId: selectedEELinkedId,
              variantId: selectedSingleVariantDetails?.variant_id,
              zonePosition: selectedProjectIsZonal
                ? selectedZoneLinked
                : undefined,
              pageNo: rightPage,
              pageSize: rightRowsPerPage,
            });
          }

          getSWModulesIntersectionsList({
            variantId: selectedSingleVariantDetails?.variant_id,
            projectId: Number(projectID),
            eeId: Number(selectedMainSWMDetails?.ee_id),
            swmId: Number(selectedMainSWMDetails?.swm_id),
          });

          setNetworkDialogOpen(false);
          setSelectedNetworkItem(null);
          setSelectedNetworkInterface(null);
        });
      }
    },
    [
      projectID,
      selectedSingleVariantDetails,
      selectedMainSWMDetails,
      selectedEEMainId,
      selectedEELinkedId,
      selectedNetworkInterface,
      selectedProjectIsZonal,
      selectedZoneMain,
      selectedZoneLinked,
      leftPage,
      leftRowsPerPage,
      rightPage,
      rightRowsPerPage,
      dispatch,
      getMainSWModulesList,
      getLinkedSWModulesList,
      getSWModulesIntersectionsList,
    ],
  );

  const handleSWModuleUnLink = useCallback(
    (selectedLinkedSWModuleDetails) => {
      if (
        projectID &&
        selectedMainSWMDetails &&
        selectedMainSWMDetails?.ee_id &&
        selectedMainSWMDetails?.swm_id &&
        selectedSingleVariantDetails &&
        selectedSingleVariantDetails?.variant_id &&
        selectedLinkedSWModuleDetails &&
        selectedLinkedSWModuleDetails?.swm_id
      ) {
        let currentTime = new Date().toISOString();

        dispatch(
          HandleApiActions({
            ...labCar.unlink_sw_module,
            params: {
              project_id: Number(projectID),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(selectedMainSWMDetails?.ee_id),
              swm_id: Number(selectedMainSWMDetails?.swm_id),
              removing_swm_id: Number(selectedLinkedSWModuleDetails?.swm_id),
              current_time: currentTime,
            },
            show_toast: true,
          }),
        ).then(() => {
          if (projectID && selectedEEMainId) {
            getMainSWModulesList({
              projectId: projectID,
              eeId: selectedEEMainId,
              variantId: selectedSingleVariantDetails?.variant_id,
              zonePosition: selectedProjectIsZonal
                ? selectedZoneMain
                : undefined,
              pageNo: leftPage,
              pageSize: leftRowsPerPage,
            });
          }
          if (
            projectID &&
            selectedEELinkedId &&
            selectedSingleVariantDetails &&
            selectedSingleVariantDetails?.variant_id
          ) {
            getLinkedSWModulesList({
              projectId: projectID,
              eeId: selectedEELinkedId,
              variantId: selectedSingleVariantDetails?.variant_id,
              zonePosition: selectedProjectIsZonal
                ? selectedZoneLinked
                : undefined,
              pageNo: rightPage,
              pageSize: rightRowsPerPage,
            });
          }

          getSWModulesIntersectionsList({
            projectId: Number(projectID),
            eeId: Number(selectedMainSWMDetails?.ee_id),
            variantId: selectedSingleVariantDetails?.variant_id,
            swmId: Number(selectedMainSWMDetails?.swm_id),
          });
        });
      }
    },
    [
      projectID,
      selectedMainSWMDetails,
      selectedSingleVariantDetails,
      selectedEEMainId,
      selectedEELinkedId,
      selectedProjectIsZonal,
      selectedZoneMain,
      selectedZoneLinked,
      leftPage,
      leftRowsPerPage,
      rightPage,
      rightRowsPerPage,
      dispatch,
      getMainSWModulesList,
      getLinkedSWModulesList,
      getSWModulesIntersectionsList,
    ],
  );

  /* ===== Zone Setup useEffect ===== */
  // Initialize zones and select first zone by default for zonal projects (for both left and right)
  // useEffect(() => {
  //   if (selectedProjectIsZonal && selectedProjectZonesList?.length > 0) {
  //     const filteredZones = selectedProjectZonesList
  //       ?.map((el) => ({
  //         value: el?.position,
  //         label: el?.zone_name,
  //       }))
  //       ?.sort((a, b) => a.label.localeCompare(b.label));

  //     setZoneOptionsList(filteredZones);

  //     // Prefer zone from URL (supports value or label match), else fallback to first
  //     const findZoneFromUrl = () => {
  //       if (!zoneFromurl) return null;
  //       const lower = String(zoneFromurl).toLowerCase();
  //       return (
  //         filteredZones.find((z) => String(z.value) === String(zoneFromurl)) ||
  //         filteredZones.find((z) => String(z.label).toLowerCase() === lower) ||
  //         null
  //       );
  //     };

  //     const urlZone = findZoneFromUrl();
  //     const fallbackZone = filteredZones?.[0] || null;

  //     const isMainValid = filteredZones.some(
  //       (z) => String(z.value) === String(selectedZoneMain),
  //     );
  //     const isLinkedValid = filteredZones.some(
  //       (z) => String(z.value) === String(selectedZoneLinked),
  //     );

  //     if (!selectedZoneMain || !isMainValid) {
  //       const setVal = urlZone?.value ?? fallbackZone?.value ?? null;
  //       if (setVal !== null) {
  //         setSelectedZoneMain(setVal);
  //         customConsole({
  //           title: "✅ ZONAL PROJECT - Zone Auto-selected",
  //           zone: urlZone?.label || fallbackZone?.label,
  //           source: urlZone ? "url" : "first",
  //         });
  //       }
  //     }
  //     if (!selectedZoneLinked || !isLinkedValid) {
  //       const setVal = urlZone?.value ?? fallbackZone?.value ?? null;
  //       if (setVal !== null) {
  //         setSelectedZoneLinked(setVal);
  //         setIsInitialized(true);
  //       }
  //     }
  //   }
  //   if (!selectedProjectIsZonal) {
  //     setSelectedZoneMain(null);
  //     setSelectedZoneLinked(null);
  //   }
  // }, [
  //   selectedProjectIsZonal,
  //   selectedProjectZonesList,
  //   selectedZoneMain,
  //   selectedZoneLinked,
  //   zoneFromurl,
  // ]);

  useEffect(() => {
    const getFilteredZones = (zones) =>
      zones
        ?.map((el) => ({
          value: el?.position,
          label: el?.zone_name,
        }))
        ?.sort((a, b) => a.label.localeCompare(b.label));

    const findZoneFromUrl = (filteredZones) => {
      if (!zoneFromurl) return null;

      const lower = String(zoneFromurl).toLowerCase();

      return (
        filteredZones.find((z) => String(z.value) === String(zoneFromurl)) ||
        filteredZones.find((z) => String(z.label).toLowerCase() === lower) ||
        null
      );
    };

    const isValidZone = (zones, value) =>
      zones.some((z) => String(z.value) === String(value));

    const setZone = (setter, value) => {
      if (value !== null) setter(value);
    };

    // ✅ EARLY EXIT
    if (!selectedProjectIsZonal) {
      setSelectedZoneMain(null);
      setSelectedZoneLinked(null);
      return;
    }

    if (!selectedProjectZonesList?.length) return;

    const filteredZones = getFilteredZones(selectedProjectZonesList);
    setZoneOptionsList(filteredZones);

    const urlZone = findZoneFromUrl(filteredZones);
    const fallbackZone = filteredZones?.[0] || null;
    const setVal = urlZone?.value ?? fallbackZone?.value ?? null;

    const isMainValid = isValidZone(filteredZones, selectedZoneMain);
    const isLinkedValid = isValidZone(filteredZones, selectedZoneLinked);

    const shouldSetMain = !selectedZoneMain || !isMainValid;
    const shouldSetLinked = !selectedZoneLinked || !isLinkedValid;

    if (shouldSetMain) {
      setZone(setSelectedZoneMain, setVal);

      if (setVal !== null) {
        customConsole({
          title: "✅ ZONAL PROJECT - Zone Auto-selected",
          zone: urlZone?.label || fallbackZone?.label,
          source: urlZone ? "url" : "first",
        });
      }
    }

    if (shouldSetLinked) {
      setZone(setSelectedZoneLinked, setVal);

      if (setVal !== null) {
        setIsInitialized(true);
      }
    }
  }, [
    selectedProjectIsZonal,
    selectedProjectZonesList,
    selectedZoneMain,
    selectedZoneLinked,
    zoneFromurl,
  ]);

  useEffect(() => {
    if (
      !selectedProjectIsZonal &&
      projectID &&
      selectedSingleVariantDetails?.variant_id &&
      isInitialized === false
    ) {
      // Clear previous selections when switching to centralized
      setSelectedEEMainId(null);
      setSelectedEEComponentMain({});
      setSelectedEELinkedId(null);
      setSelectedEEComponentLinked({});
      setSWMComponentMainList([]);
      setSWMComponentsLinkedList([]);
      setSelectedMainSWMDetails(null);

      // Fetch EE components for centralized projects (populate both left/right lists)
      (async () => {
        const resp = await getEEMainLinkedComponentsList({
          projectId: projectID,
          variantId: selectedSingleVariantDetails?.variant_id,
        });

        // If we got data, auto-select first EE for both left and right if not already selected
        if (!resp?.error && resp?.data?.length > 0) {
          const byUrl = eeFromurl
            ? resp.data.find((d) => String(d.ee_id) === String(eeFromurl))
            : null;
          const pick = eeFromurl ? byUrl : resp.data[0];

          if (pick) {
            setSelectedEEMainId(pick.ee_id);
            setSelectedEEComponentMain(pick);
            setSelectedEELinkedId(pick.ee_id);
            setSelectedEEComponentLinked(pick);
          }
        }

        setIsInitialized(true);

        customConsole({
          title: "✅ CENTRALIZED PROJECT INITIALIZED",
          projectId: projectID,
          variantId: selectedSingleVariantDetails?.variant_id,
          message: "Fetched EE components for centralized project",
        });
      })();
    }
  }, [
    projectID,
    selectedProjectIsZonal,
    selectedSingleVariantDetails,
    getEEMainLinkedComponentsList,
    isInitialized,
    eeFromurl,
  ]);

  // Fetch EE components for LEFT side when zone/project type changes
  useEffect(() => {
    if (selectedProjectIsZonal) {
      setEEComponentsMainList([]);
      setSelectedEEMainId(null);
      setSelectedEEComponentMain({});
      setSWMComponentMainList([]);
      setSelectedMainSWMDetails(null);
      if (
        projectID &&
        selectedZoneMain &&
        selectedSingleVariantDetails?.variant_id
      ) {
        // Debounce rapid zone changes to avoid duplicate API calls
        if (leftZoneTimerRef.current) clearTimeout(leftZoneTimerRef.current);
        leftZoneTimerRef.current = setTimeout(async () => {
          const resp = await getEEMainComponentsList({
            projectId: projectID,
            variantId: selectedSingleVariantDetails?.variant_id,
            zonePosition: selectedZoneMain,
          });
          if (!resp?.error && resp?.data?.length > 0) {
            const byUrl = eeFromurl
              ? resp.data.find((d) => String(d.ee_id) === String(eeFromurl))
              : null;
            const pick = eeFromurl ? byUrl : resp.data[0];

            if (pick) {
              setSelectedEEMainId(pick.ee_id);
              setSelectedEEComponentMain(pick);
            }
          }
        }, 250);
      }
    }
    return () => {
      if (leftZoneTimerRef.current) {
        clearTimeout(leftZoneTimerRef.current);
        leftZoneTimerRef.current = null;
      }
    };
  }, [
    selectedZoneMain,
    selectedProjectIsZonal,
    projectID,
    selectedSingleVariantDetails,
    getEEMainComponentsList,
    eeFromurl,
  ]);

  // Fetch EE components for RIGHT side when zone/project type changes
  useEffect(() => {
    if (selectedProjectIsZonal) {
      setEEComponentsLinkedList([]);
      setSelectedEELinkedId(null);
      setSelectedEEComponentLinked({});
      setSWMComponentsLinkedList([]);
      if (
        projectID &&
        selectedZoneLinked &&
        selectedSingleVariantDetails?.variant_id
      ) {
        // Debounce rapid zone changes to avoid duplicate API calls
        if (rightZoneTimerRef.current) clearTimeout(rightZoneTimerRef.current);
        rightZoneTimerRef.current = setTimeout(async () => {
          const resp = await getEELinkedComponentsList({
            projectId: projectID,
            zonePosition: selectedZoneLinked,
            variantId: selectedSingleVariantDetails?.variant_id,
          });
          if (!resp?.error && resp?.data?.length > 0) {
            const first = resp.data[0];
            if (first) {
              setSelectedEELinkedId(first.ee_id);
              setSelectedEEComponentLinked(first);
            }
          }
        }, 250);
      }
      return () => {
        if (rightZoneTimerRef.current) {
          clearTimeout(rightZoneTimerRef.current);
          rightZoneTimerRef.current = null;
        }
      };
    }
  }, [
    selectedZoneLinked,
    selectedProjectIsZonal,
    projectID,
    selectedSingleVariantDetails,
    getEELinkedComponentsList,
  ]);

  /* ===== Memoized Params for API Calls ===== */

  // Memoize left card EE autocomplete params
  const leftEEAutocompleteParams = useMemo(() => {
    return {
      ...(projectID &&
        variantID &&
        (!selectedProjectIsZonal || selectedZoneMain)
        ? {
          project_id: Number(projectID),
          variant_id: Number(variantID),
          ...(selectedProjectIsZonal && { zone_position: selectedZoneMain }),
          status: true,
          page_number: 1,
          page_size: 10,
        }
        : {}),
    };
  }, [projectID, variantID, selectedProjectIsZonal, selectedZoneMain]);

  // Memoize left card SWM search params
  const leftSWMSearchParams = useMemo(() => {
    return {
      ...(projectID && variantID && selectedEEMainId
        ? {
          project_id: Number(projectID),
          variant_id: Number(variantID),
          ee_id: Number(selectedEEMainId),
          ...(swmFromurl ? { swm_id: Number(swmFromurl) } : {}),
          ...(selectedProjectIsZonal && { zone_position: selectedZoneMain }),
          status: true,
          page_number: leftPage,
          page_size: leftRowsPerPage,
        }
        : {}),
    };
  }, [
    projectID,
    variantID,
    selectedEEMainId,
    selectedProjectIsZonal,
    selectedZoneMain,
    leftPage,
    leftRowsPerPage,
    swmFromurl,
  ]);

  // Memoize right card EE autocomplete params
  const rightEEAutocompleteParams = useMemo(() => {
    return {
      ...(projectID &&
        variantID &&
        (!selectedProjectIsZonal || selectedZoneLinked)
        ? {
          project_id: Number(projectID),
          variant_id: Number(variantID),
          ...(selectedProjectIsZonal && {
            zone_position: selectedZoneLinked,
          }),
          status: true,
          page_number: 1,
          page_size: 10,
        }
        : {}),
    };
  }, [projectID, variantID, selectedProjectIsZonal, selectedZoneLinked]);

  // Memoize right card SWM search params
  const rightSWMSearchParams = useMemo(() => {
    return {
      ...(projectID && variantID && selectedEELinkedId
        ? {
          project_id: Number(projectID),
          variant_id: Number(variantID),
          ee_id: Number(selectedEELinkedId),
          ...(selectedProjectIsZonal && {
            zone_position: selectedZoneLinked,
          }),
          status: true,
          page_number: rightPage,
          page_size: rightRowsPerPage,
        }
        : {}),
    };
  }, [
    projectID,
    variantID,
    selectedEELinkedId,
    selectedProjectIsZonal,
    selectedZoneLinked,
    rightPage,
    rightRowsPerPage,
  ]);

  /* ===== Auto-select First EE Component for Main/Left ===== */
  useEffect(() => {
    if (eeComponentsMainList?.length > 0 && !selectedEEMainId) {
      const matchingComponent = eeFromurl
        ? eeComponentsMainList?.find(
          (item) => String(item?.ee_id) === String(eeFromurl),
        )
        : null;

      if (matchingComponent) {
        setSelectedEEMainId(matchingComponent?.ee_id);
        setSelectedEEComponentMain(matchingComponent);
        customConsole({
          title: "✅ LEFT EE AUTO-SELECTED (from URL)",
          eeId: matchingComponent?.ee_id,
          description: matchingComponent?.description,
        });
      } else {
        // Auto-select first EE component (works for centralized & zonal)
        const first = eeComponentsMainList?.[0];
        if (first) {
          setSelectedEEMainId(first.ee_id);
          setSelectedEEComponentMain(first);
          customConsole({
            title: "✅ LEFT EE AUTO-SELECTED (first item)",
            eeId: first.ee_id,
            description: first.description,
            isZonal: selectedProjectIsZonal,
          });
        }
      }
    }
  }, [
    eeComponentsMainList,
    eeFromurl,
    selectedEEMainId,
    selectedProjectIsZonal,
  ]);

  useEffect(() => {
    if (!selectedEEMainId || eeComponentsMainList?.length === 0) return;

    const matchingComponent = eeComponentsMainList.find(
      (item) => String(item?.ee_id) === String(selectedEEMainId),
    );

    if (matchingComponent) {
      setSelectedEEComponentMain(matchingComponent);
    }
  }, [eeComponentsMainList, selectedEEMainId]);

  useEffect(() => {

    console.log(
      "[SWM] eeComponentsMainList ids:",
      eeComponentsMainList?.map((item) => item?.ee_id),
    );
  }, [eeFromurl, selectedEEMainId, eeComponentsMainList]);

  /* ===== Auto-select First EE Component for Linked/Right ===== */
  useEffect(() => {
    if (eeComponentsLinkedList?.length > 0 && !selectedEELinkedId) {
      const firstLinked = eeComponentsLinkedList?.[0];
      if (firstLinked) {
        setSelectedEELinkedId(firstLinked.ee_id);
        setSelectedEEComponentLinked(firstLinked);
        customConsole({
          title: "✅ RIGHT EE AUTO-SELECTED (first item)",
          eeId: firstLinked.ee_id,
          description: firstLinked.description,
          isZonal: selectedProjectIsZonal,
        });
      }
    }
  }, [eeComponentsLinkedList, selectedEELinkedId, selectedProjectIsZonal]);

  /* ===== SWM Setup useEffects ===== */
  // SWM lists will be driven by the search bars' param changes

  // Ensure first SWM auto-selects after EE change by clearing previous selection
  useEffect(() => {
    if (selectedEEMainId) {
      setSelectedMainSWMDetails(null);
      setSWMComponentMainList([]);
      setSwmMainCount(0);
      setLeftPage(1);
    }
  }, [selectedEEMainId]);

  useEffect(() => {
    if (selectedEELinkedId) {
      setSWMComponentsLinkedList([]);
      setSwmLinkCount(0);
      setRightPage(1);
    }
  }, [selectedEELinkedId]);

  // Fetch SWM list when EE selection / pagination changes
  useEffect(() => {
    if (
      projectID &&
      variantID &&
      selectedEEMainId &&
      (!selectedProjectIsZonal || selectedZoneMain)
    ) {
      getMainSWModulesList({
        projectId: projectID,
        eeId: selectedEEMainId,
        variantId: variantID,
        zonePosition: selectedProjectIsZonal ? selectedZoneMain : undefined,
        pageNo: leftPage,
        pageSize: leftRowsPerPage,
      });
    }
  }, [
    projectID,
    variantID,
    selectedEEMainId,
    selectedProjectIsZonal,
    selectedZoneMain,
    leftPage,
    leftRowsPerPage,
    getMainSWModulesList,
  ]);

  // Fetch RIGHT SWM list when linked EE / pagination changes
  useEffect(() => {
    if (
      projectID &&
      variantID &&
      selectedEELinkedId &&
      (!selectedProjectIsZonal || selectedZoneLinked)
    ) {
      getLinkedSWModulesList({
        projectId: projectID,
        eeId: selectedEELinkedId,
        variantId: variantID,
        zonePosition: selectedProjectIsZonal ? selectedZoneLinked : undefined,
        pageNo: rightPage,
        pageSize: rightRowsPerPage,
      });
    }
  }, [
    projectID,
    variantID,
    selectedEELinkedId,
    selectedProjectIsZonal,
    selectedZoneLinked,
    rightPage,
    rightRowsPerPage,
    getLinkedSWModulesList,
  ]);

  /* ===== Auto-select First SWM for Main/Left ===== */
  useEffect(() => {
    if (SWMComponentMainList?.length > 0) {
      const matchingSwm = swmFromurl
        ? SWMComponentMainList.find(
          (item) => String(item?.swm_id) === String(swmFromurl),
        )
        : null;

      if (matchingSwm) {
        setSelectedMainSWMDetails(matchingSwm);
      } else if (!selectedMainSWMDetails) {
        // Only auto-select first SWM if no component is currently selected
        setSelectedMainSWMDetails(SWMComponentMainList?.[0]);
      }
    }
  }, [SWMComponentMainList, swmFromurl, selectedMainSWMDetails]);

  // Fetch intersections only when selected SWM changes (not on intermediate renders)
  useEffect(() => {
    if (selectedMainSWMDetails?.swm_id) {
      getSWModulesIntersectionsList({
        variantId: selectedSingleVariantDetails?.variant_id,
        projectId: Number(projectID),
        eeId: Number(selectedEEMainId),
        swmId: Number(selectedMainSWMDetails?.swm_id),
      });
    }
  }, [
    projectID,
    selectedEEMainId,
    selectedMainSWMDetails?.swm_id,
    selectedSingleVariantDetails,
    getSWModulesIntersectionsList,
  ]);

  /* ===== UI/Display Modifications ===== */

  useEffect(() => {
    const modifiedList = SWMComponentMainList.map((item) => ({
      data: item,
      id: item.swm_id,
      cardDataArray: [
        {
          key: "Number",
          value: (
            <CustomToolTip
              type="string"
              length={50}
              style={{
                ...componentStyle.font.text,
                fontWeight: "bold",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={`Name: ${item?.name ?? ""}`}
            >
              {`${item?.name ?? ""}`}
            </CustomToolTip>
          ),
          keyStyle: { ...componentStyle.font.text, fontWeight: "bold" },
          valueStyle: { ...componentStyle.font.text, fontWeight: "bold" },
        },
        {
          key: "Description",
          value: (
            <CustomToolTip
              type="string"
              length={50}
              style={{
                ...componentStyle.font.text,
                // fontWeight: "bold",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={`Description: ${item?.description ?? ""}`}
            >
              {`${item?.description ?? ""}`}
            </CustomToolTip>
          ),
        },
        {
          key: "Version",
          value: (
            <CustomToolTip
              type="string"
              length={50}
              style={{
                ...componentStyle.font.text,
                // fontWeight: "bold",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={`Version: ${item?.version ?? ""}`}
            >
              {`${item?.version ?? ""}`}
            </CustomToolTip>
          ),
        },
      ],
    }));
    setModifiedSWMComponentList(modifiedList);
  }, [SWMComponentMainList]);

  useEffect(() => {
    // Filter out the selected left component from right side list
    const filteredList = SWMComponentsLinkedList.filter(
      (item) => item.swm_id !== selectedMainSWMDetails?.swm_id,
    );
    const modifiedList = filteredList.map((item) => {
      const isLinked = item?.linked_swm_ids?.includes(
        selectedMainSWMDetails?.swm_id,
      );
      return {
        data: item,
        id: item.swm_id,
        cardDataArray: [
          {
            key: "Number",
            value: (
              <CustomToolTip
                type="string"
                length={50}
                style={{
                  ...componentStyle.font.text,
                  fontWeight: "bold",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={`Name: ${item?.name ?? ""}`}
              >
                {`${item?.name ?? ""}`}
              </CustomToolTip>
            ),
            keyStyle: { ...componentStyle.font.text, fontWeight: "bold" },
            valueStyle: { ...componentStyle.font.text, fontWeight: "bold" },
          },
          {
            key: "Description",
            value: (
              <CustomToolTip
                type="string"
                length={50}
                style={{
                  ...componentStyle.font.text,
                  // fontWeight: "bold",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={`Description: ${item?.description ?? ""}`}
              >
                {`${item?.description ?? ""}`}
              </CustomToolTip>
            ),
          },
          {
            key: "Version",
            value: (
              <CustomToolTip
                type="string"
                length={50}
                style={{
                  ...componentStyle.font.text,
                  // fontWeight: "bold",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={`Version: ${item?.version ?? ""}`}
              >
                {`${item?.version ?? ""}`}
              </CustomToolTip>
            ),
          },
        ],
        cardStyle: isLinked
          ? {
            border: "1px solid #4CAF50",
            backgroundColor: "rgba(76, 175, 80, 0.05)",
          }
          : {},
      };
    });
    setModifiedSWMComponentLinkList(modifiedList);
  }, [SWMComponentsLinkedList, selectedMainSWMDetails]);

  /* ===== Dialog Handlers ===== */

  const handleNetworkInterfaceSelect = (item) => {
    handleSWModuleLink(item);
    setNetworkDialogOpen(false);
    setSelectedNetworkItem(null);
    setSelectedNetworkInterface(null);
  };

  const handleCloseNetworkInterface = () => {
    setNetworkDialogOpen(false);
    setSelectedNetworkItem(null);
    setSelectedNetworkInterface(null);
  };

  const handleOpenNetworkInterface = (item) => {
    setSelectedNetworkItem(item);
    setNetworkDialogOpen(true);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    const params = new URLSearchParams(location.search);
    params.delete("swm_id");
    if (!params.get("ee_id")) {
      params.set(
        "ee_id",
        selectedEEMainId || selectedEEComponentMain?.ee_id || "",
      );
    }
    if (!params.get("selectedZone")) {
      params.set("selectedZone", selectedZoneMain || "");
    }
    params.set("tabFromurl", "1");
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleActionType2DialogOpen = () => {
    setActionType2DialogOpen(true);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handlePendingSWMActionDialogClose = () => {
    setPendingSWMActionDialogOpen(false);
    setSelectedPendingSWMAction(null);
  };

  const handleRedirectionToSWMAction = () => {
    const selectedSWM = selectedPendingSWMAction || selectedMainSWMDetails;
    const selectedZoneDetails = selectedProjectZonesList?.find(
      (zone) => Number(zone?.position) === Number(selectedZoneMain),
    );
    const params = new URLSearchParams();

    if (selectedSWM?.ee_id || selectedEEMainId) {
      params.set("ee_id", selectedSWM?.ee_id || selectedEEMainId);
    }
    if (selectedSWM?.swm_id) {
      params.set("swm_id", selectedSWM.swm_id);
    }
    params.set("actionMode", "swmAction");
    if (selectedZoneMain) {
      params.set(
        "selectedZone",
        selectedZoneDetails?.zone_name || selectedZoneMain,
      );
      params.set("zonePosition", selectedZoneMain);
    }
    if (leftPage) {
      params.set("page", leftPage);
    }

    navigate(`/dashboard/v2/EESWM?${params.toString()}`);
    handlePendingSWMActionDialogClose();
  };

  const getSWMActionTypeTooltip = (actionType) => {
    switch (Number(actionType)) {
      case 1:
        return "Action Pending in Software to Software linking";
      case 2:
        return "Action Pending in Software to Legal linking";
      case 4:
        return "Action required in Software Module Attribute";
      case 5:
        return "Action pending in Software Module Version";
      default:
        return "";
    }
  };

  const handleSWMActionTypeClick = (event, item) => {
    event.stopPropagation();
    setSelectedMainSWMDetails(item);

    if (Number(item?.action_type) === 1) {
      handleDialogOpen();
    } else if (Number(item?.action_type) === 2) {
      handleActionType2DialogOpen();
    } else if ([4, 5].includes(Number(item?.action_type))) {
      setSelectedPendingSWMAction(item);
      setPendingSWMActionDialogOpen(true);
    }
  };

  const handleZoneMainChange = (e) => {
    setSelectedZoneMain(e.target.value);
  };

  const handleZoneLinkedChange = (e) => {
    setSelectedZoneLinked(e.target.value);
  };

  const confirmUpdateAndRedirect = async (selectedMainSWModuleDetails) => {
    const reqObj = {
      current_time: new Date().toISOString(),
      project_id: Number(projectID),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEMainId),
      swm_id: Number(selectedMainSWModuleDetails?.swm_id),
      is_dependency_linking: true,
    };
    try {
      const response = await dispatch(
        HandleApiActions({
          ...labCar.update_action_type,
          params: reqObj,
          show_toast: true,
        }),
      );
      getMainSWModulesList({
        projectId: projectID,
        eeId: selectedEEMainId,
        variantId: selectedSingleVariantDetails?.variant_id,
      });
      handleDialogClose();
      handleActionType2DialogOpen();
      if (!response.error) {
      }
    } catch (error) {
    }
  };

  // recreate function
  const handleRecreateDialogOpen = async (eeDetails) => {
    let parentSWMLinkResponse = await dispatch(
      HandleApiActions({
        ...labCar.get_parent_swm_link,
        params: {
          project_id: selectedProjectDetails.project_id,
          parent_project_id: selectedProjectDetails.parent_project_id,
          swm_id: eeDetails?.swm_id,
          parent_swm_id: eeDetails?.parent_swm_id,
        },
      }),
    );
    if (!parentSWMLinkResponse?.error) {
      let responseList = parentSWMLinkResponse?.data;
      // create data to render in table
      let tableRows = responseList.map((swm) => {
        return {
          id: swm.swm_id,
          component: {
            primary: {
              key: "Name:",
              value: swm.name,
            },
            secondary: [
              {
                key: "Description:",
                value: swm.description,
              },
              {
                key: "version:",
                value: swm.version,
              },
            ],
          },
          raw: swm,
        };
      });
      setRecreateSWMLinkList(tableRows);
    } else {
      setRecreateSWMLinkList([]);
    }
    setOpenSWMLinkRecreateDialog(true);
  };

  const closeRecreateDialog = () => {
    setOpenSWMLinkRecreateDialog(false);

    if (projectID && selectedEEMainId) {
      getMainSWModulesList({
        projectId: projectID,
        eeId: selectedEEMainId,
        variantId: selectedSingleVariantDetails?.variant_id,
        zonePosition: selectedProjectIsZonal ? selectedZoneMain : undefined,
        pageNo: leftPage,
        pageSize: leftRowsPerPage,
      });
    }
    if (
      projectID &&
      selectedEELinkedId &&
      selectedSingleVariantDetails?.variant_id &&
      selectedSingleVariantDetails
    ) {
      getLinkedSWModulesList({
        projectId: projectID,
        eeId: selectedEELinkedId,
        variantId: selectedSingleVariantDetails?.variant_id,
        zonePosition: selectedProjectIsZonal ? selectedZoneLinked : undefined,
        pageNo: rightPage,
        pageSize: rightRowsPerPage,
      });
    }

    getSWModulesIntersectionsList({
      variantId: selectedSingleVariantDetails?.variant_id,
      projectId: Number(projectID),
      eeId: Number(selectedMainSWMDetails?.ee_id),
      swmId: Number(selectedMainSWMDetails?.swm_id),
    });
  };

  const handleOnClickRecreateLink = (selectedSWM) => {
    setNetworkDialogOpen(true);
    setSelectedRecreateSWMForLink(selectedSWM);

    setSelectedNetworkInterface(selectedSWM?.parent_network_interface);
  };

  const handleRecreateLink = async () => {
    const reqObj = {
      project_id: Number(projectID),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      parent: {
        ee_id: Number(selectedMainSWMDetails?.ee_id),
        swm_id: Number(selectedMainSWMDetails?.swm_id),
      },
      child: {
        ee_id: Number(selectedRecreateSWMForLink?.ee_id),
        swm_id: Number(selectedRecreateSWMForLink?.swm_id),
      },
      current_time: new Date().toISOString(),
      network_interface: selectedNetworkInterface,
      module_type: "swm",
      parent_parent_swm_id: selectedMainSWMDetails?.parent_swm_id,
      parent_child_swm_id: selectedRecreateSWMForLink?.parent_swm_id,
      session_id: selectedRecreateSWMForLink?.session_id,
    };
    // hit post api for recreate link
    let apiDetails = labCar.recreate_swm_link;
    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: reqObj,
        show_toast: true,
      }),
    );
    if (!resp.error) {
      setNetworkDialogOpen(false);
      handleRecreateDialogOpen(selectedMainSWMDetails);
    }
  };

  const handleSWMViewButtonClick = (item) => {
    setSelectedComponent({
      swm_details: item,
      ee_details: { ee_id: item?.ee_id },
    });
    setApiAction(softwareIntersectionAPI.get_software_intersections);
    setLinkApiProps({
      swm_id: item?.swm_id,
      ee_id: item?.ee_id,
    });
    setApiDataKey("linked_swm_details");
    setModuleViewMoreType("SWM");
    toggleSWMDialog(true)();
  };

  const toggleSWMDialog = (newOpen) => () => {
    setIsSWMDialogOpen(newOpen);
  };

  return (
    <Box key={swmFromurl}>
      <ComponentLinkTemplate
        leftCardHeader={
          <Box>
            {/* First line: title */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                mb: 0.5,
              }}
            >
              <Typography>Select Module</Typography>
            </Box>
            {/* Second line: zone + EE dropdown */}
            <Box
              display="flex"
              gap={1}
              sx={{
                // width: "100%",
                mt: 0.5,
                justifyContent: "space-between",
                alignItems: "center",
                mr: selectedProjectIsZonal ? 1 : 0,
              }}
            >
              {selectedProjectIsZonal && (
                <Box sx={{ flex: "0 0 40%" }}>
                  <CustomSelectV2
                    label={"Select Zone"}
                    optionsList={zoneOptionsList}
                    selectedValue={selectedZoneMain}
                    handleOnChange={handleZoneMainChange}
                    dark={true}
                    showLabel={false}
                    sx={{
                      height: 40,
                      border: "1px solid #ced4da",
                      borderRadius: 1,
                      background: "#fff",
                      ...componentStyle.searchField.textField,
                    }}
                  />
                </Box>
              )}
              <Box sx={{ flex: selectedProjectIsZonal ? "0 0 60%" : "1" }}>
                <CustomAutoCompleteWithAPIV2
                  key={`left-ee-${variantID}-${selectedZoneMain}`}
                  apiAction={labCar.get_ee_components_new}
                  searchKey={"description"}
                  placeholder={"Search EE Component"}
                  skipInitialSearch={false}
                  firstItemDefaultSelect={!eeFromurl}
                  getSelectedItem={(selectedEE) => {
                    setSelectedEEComponentMain(selectedEE);
                    setSelectedEEMainId(selectedEE?.ee_id);
                  }}
                  displayKey="description"
                  valueObj={
                    selectedProjectIsZonal
                      ? selectedEEComponentMain
                      : eeComponentsMainList?.some(
                        (ee) =>
                          String(ee?.ee_id) === String(selectedEEMainId),
                      )
                        ? selectedEEComponentMain
                        : {}
                  }
                  enableDarkMode={true}
                  txtFldStyle={componentStyle.searchField.textField}
                  sx={{
                    height: 40,
                    border: "1px solid #ced4da",
                    borderRadius: 1,
                    background: "#fff",
                    ...componentStyle.searchField.textField,
                  }}
                  skipApiCall={!projectID || !variantID}
                  // skipInitialSearch={selectedProjectIsZonal}
                  params={leftEEAutocompleteParams}
                />
              </Box>
            </Box>
            {/* Third line: software search */}
            <Box sx={{ width: "100%", mt: 0.5 }}>
              <CustomSearchbarWithApiV2
                key={`left-swm-${selectedEEComponentMain?.ee_id}`}
                apiAction={labCar.get_sw_modules_new}
                searchKey={"name"}
                placeholder={"Search Number"}
                getDataOnSearch={(data, resp) => {
                  if (resp.error) {
                    setSWMComponentMainList([]);
                    setSwmMainCount(0);
                  } else {
                    setSWMComponentMainList(resp.data);
                    setSwmMainCount(resp.total_count || resp.data?.length || 0);
                  }
                }}
                skipApiCall={
                  !projectID ||
                  !variantID ||
                  !selectedEEMainId ||
                  (selectedProjectIsZonal && !selectedZoneMain)
                }
                skipInitialSearch={true}
                params={leftSWMSearchParams}
                iconColor={"white"}
                txtFldStyle={componentStyle.searchField.textField}
                sx={{
                  height: 40,
                  border: "1px solid #ced4da",
                  borderRadius: 1,
                  background: "#fff",
                  ...componentStyle.searchField.textField,
                }}
              />
            </Box>
          </Box>
        }
        leftDataList={modifiedSWMComponentList}
        leftHandleSelectedComponent={(selectedObj) => {
          setSelectedMainSWMDetails(selectedObj.data);
        }}
        leftSelectedComponentDetails={selectedMainSWMDetails}
        leftRenderCardActionIcons={(item) => {
          const actionType = Number(item?.action_type);
          const actionTypeTooltip = getSWMActionTypeTooltip(actionType);
          return (
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5, // space between icons
                }}
              >
                {[1, 2, 4, 5].includes(actionType) && (
                  <CustomToolTip
                    type="component"
                    title={actionTypeTooltip}
                  >
                    <IconButton
                      size="small"
                      sx={{ color: "black", p: 0.5 }}
                      onClick={(event) => handleSWMActionTypeClick(event, item)}
                    >
                      <ErrorOutlineIcon sx={{ fontSize: "15px" }} />
                    </IconButton>
                  </CustomToolTip>
                )}
                {item.parent_swm_id && (
                  <CustomToolTip type="component" title="Recreate Link">
                    <IconButton
                      size="small"
                      sx={{ color: "black" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedMainSWMDetails(item);
                        handleRecreateDialogOpen(item);
                      }}
                    >
                      <RefreshIcon sx={{ fontSize: "15px", color: "black" }} />
                    </IconButton>
                  </CustomToolTip>
                )}
                <CustomToolTip type="component" title="Initiate Smart-matching">
                  <IconButton
                    size="small"
                    sx={{ color: "black" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedMainSWMDetails(item);
                      handleSmartMatchRequest(item);
                    }}
                  >
                    <AutoAwesomeIcon
                      sx={{ fontSize: "15px", color: "black" }}
                    />
                  </IconButton>
                </CustomToolTip>
                <IconButton
                  size="small"
                  sx={{ color: "#000000", p: 0.5 }}
                  onClick={(event) => {
                    handleSWMViewButtonClick(item);
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: "15px", color: "black" }} />
                </IconButton>
              </Box>
            </Box>
          );
        }}
        leftPage={leftPage}
        leftRowsPerPage={leftRowsPerPage}
        leftHandlePageChange={setLeftPage}
        leftTotalCount={swmMainCount}
        leftCardID={"swm_id"}
        // Right data
        rightCardHeader={
          <Box>
            {/* First line: title */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              sx={{ mb: 0.5 }}
            >
              <Typography>Link to Module</Typography>
            </Box>
            {/* Second line: zone + EE dropdown */}
            <Box
              display="flex"
              gap={1}
              sx={{
                // width: "100%",
                mt: 0.5,
                justifyContent: "space-between",
                alignItems: "center",
                mr: selectedProjectIsZonal ? 1 : 0,
              }}
            >
              {selectedProjectIsZonal && (
                <Box sx={{ flex: "0 0 40%" }}>
                  <CustomSelectV2
                    label={"Select Zone"}
                    optionsList={zoneOptionsList}
                    selectedValue={selectedZoneLinked}
                    handleOnChange={handleZoneLinkedChange}
                    dark={true}
                    showLabel={false}
                    sx={{
                      height: 40,
                      border: "1px solid #ced4da",
                      borderRadius: 1,
                      background: "#fff",
                      ...componentStyle.searchField.textField,
                    }}
                  />
                </Box>
              )}
              <Box sx={{ flex: selectedProjectIsZonal ? "0 0 60%" : "1" }}>
                <CustomAutoCompleteWithAPIV2
                  key={`right-ee-${variantID}-${selectedZoneLinked}`}
                  apiAction={labCar.get_ee_components_new}
                  searchKey={"description"}
                  placeholder={"Search EE Component"}
                  // firstItemDefaultSelect={!selectedProjectIsZonal}
                  firstItemDefaultSelect={!eeFromurl}
                  skipInitialSearch={false}
                  getSelectedItem={(selectedEE) => {
                    setSelectedEEComponentLinked(selectedEE);
                    setSelectedEELinkedId(selectedEE?.ee_id);
                  }}
                  displayKey="description"
                  valueObj={
                    selectedProjectIsZonal
                      ? selectedEEComponentLinked
                      : eeComponentsLinkedList?.some(
                        (ee) =>
                          String(ee?.ee_id) === String(selectedEELinkedId),
                      )
                        ? selectedEEComponentLinked
                        : {}
                  }
                  enableDarkMode={true}
                  txtFldStyle={componentStyle.searchField.textField}
                  sx={{
                    height: 40,
                    border: "1px solid #ced4da",
                    borderRadius: 1,
                    background: "#fff",
                    ...componentStyle.searchField.textField,
                  }}
                  skipApiCall={!projectID || !variantID}
                  params={rightEEAutocompleteParams}
                />
              </Box>
            </Box>
            {/* Third line: software search */}
            <Box sx={{ width: "100%", mt: 0.5 }}>
              <CustomSearchbarWithApiV2
                key={`right-swm-${selectedEEComponentLinked?.ee_id}`}
                apiAction={labCar.get_sw_modules_new}
                searchKey={"name"}
                placeholder={"Search Number"}
                getDataOnSearch={(data, resp) => {
                  if (resp.error) {
                    setSWMComponentsLinkedList([]);
                    setSwmLinkCount(0);
                  } else {
                    setSWMComponentsLinkedList(resp.data);
                    setSwmLinkCount(resp.total_count || resp.data?.length || 0);
                  }
                }}
                skipApiCall={
                  !projectID ||
                  !variantID ||
                  !selectedEELinkedId ||
                  (selectedProjectIsZonal && !selectedZoneLinked)
                }
                skipInitialSearch={true}
                params={rightSWMSearchParams}
                iconColor={"white"}
                txtFldStyle={componentStyle.searchField.textField}
                sx={{
                  height: 40,
                  border: "1px solid #ced4da",
                  borderRadius: 1,
                  background: "#fff",
                  ...componentStyle.searchField.textField,
                }}
              />
            </Box>
          </Box>
        }
        rightDataList={modifiedSWMComponentLinkList}
        rightPage={rightPage}
        rightRowsPerPage={rightRowsPerPage}
        rightHandlePageChange={setRightPage}
        rightTotalCount={swmLinkCount}
        rightCardStyleProp={(item) => item.cardStyle || {}}
        rightRenderCardActionIcons={(item) => {
          return (
            <Box sx={{ position: "relative" }}>
              {selectedMainSWMDetails && (
                <CustomToolTip
                  type="component"
                  title={
                    item?.linked_swm_ids?.includes(
                      selectedMainSWMDetails?.swm_id,
                    )
                      ? "Unlink"
                      : "Link"
                  }
                >
                  <IconButton
                    size="small"
                    sx={{
                      fontSize: "12px",
                      color: "black",
                      position: "absolute",
                      top: 2,
                      right: 2,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        item?.linked_swm_ids?.includes(
                          selectedMainSWMDetails?.swm_id,
                        )
                      ) {
                        handleSWModuleUnLink(item);
                      } else {
                        if (
                          networkInterfacesSWM &&
                          networkInterfacesSWM.length > 0
                        ) {
                          handleOpenNetworkInterface(item);
                        } else {
                          handleSWModuleLink(item);
                        }
                      }
                    }}
                  >
                    {item?.linked_swm_ids?.includes(
                      selectedMainSWMDetails?.swm_id,
                    ) ? (
                      <RemoveCircleOutlineIcon sx={{ fontSize: 15 }} />
                    ) : (
                      <ControlPointIcon sx={{ fontSize: 15 }} />
                    )}
                  </IconButton>
                </CustomToolTip>
              )}
            </Box>
          );
        }}
      />
      {/* Network Interface Dialog */}
      {/* Dialog for network interface selection */}
      <CustomDialogBoxV2
        open={networkDialogOpen}
        onClose={handleCloseNetworkInterface}
        title={"Network Interface"}
        actionButtonStyle="continuous"
        successButtonName="Confirm"
        onClickSuccess={() => {
          if (openSWMLinkRecreateDialog) {
            handleRecreateLink();
          } else {
            handleNetworkInterfaceSelect(selectedNetworkItem);
          }
        }}
        rejectButtonName={"Cancel"}
        onClickReject={handleCloseNetworkInterface}
        onDisabled={!selectedNetworkInterface}
      >
        <Box sx={{ width: "350px" }}>
          <Stack spacing={2}>
            {networkInterfacesSWM?.length > 0 ? (
              <>
                <Stack>
                  <CustomSelectV2
                    label={"Select The Interface"}
                    optionsList={networkInterfacesSWM.map((ni) => ({
                      label: ni.name,
                      value: ni.name,
                    }))}
                    selectedValue={selectedNetworkInterface}
                    handleOnChange={(e) => {
                      setSelectedNetworkInterface(e.target.value);
                    }}
                    required={true}
                    placeholder={false}
                  />
                </Stack>
              </>
            ) : (
              <Typography sx={{ ...componentStyle.font.cardText }}>
                No network interfaces available.
              </Typography>
            )}
          </Stack>
        </Box>
      </CustomDialogBoxV2>

      {/* Action Type 1 Dialog */}
      <CustomDialogBoxV2
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={"Action Required"}
        actionButtonStyle="continuous"
        successButtonName={"Yes"}
        rejectButtonName={"No"}
        onClickSuccess={() => {
          confirmUpdateAndRedirect(selectedMainSWMDetails);
        }}
        onClickReject={handleDialogClose}
      >
        <Box sx={{ width: "300px" }}>
          <Typography>
            Are you sure the dependency linking of Software Module is completed?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      {/* Action Type 2 Dialog */}
      <CustomDialogBoxV2
        open={actionType2DialogOpen}
        onClose={handleActionType2DialogClose}
        title={"Action Required"}
        actionButtonStyle="continuous"
        successButtonName={"Link Now"}
        rejectButtonName={"Link Later"}
        // onClickSuccess={handleActionType2DialogClose}
        onClickSuccess={() => {
          navigate(
            `/dashboard/v2/type-approval/link?swm_id=${selectedMainSWMDetails?.swm_id
            }&ee_id=${selectedEEMainId}&zone=${selectedZoneMain}&tab=${1}`,
          );
        }}
        onClickReject={handleActionType2DialogClose}
      >
        <Box sx={{ width: "300px" }}>
          <Typography>
            Software Module intersection linking is pending!
          </Typography>
        </Box>
      </CustomDialogBoxV2>
      <CustomDialogBoxV2
        open={pendingSWMActionDialogOpen}
        onClose={handlePendingSWMActionDialogClose}
        title={"Action"}
        actionButtonStyle="continuous"
        successButtonName={"Link Now"}
        onClickSuccess={handleRedirectionToSWMAction}
        onClickReject={handlePendingSWMActionDialogClose}
        rejectButtonName={"Link Later"}
      >
        <Box sx={{ width: "350px", height: "50px" }}>
          <Typography>
            {Number(selectedPendingSWMAction?.action_type) === 4
              ? "Action pending in Software Module Attribute"
              : "Action pending in Software Module Version"}
          </Typography>
        </Box>
      </CustomDialogBoxV2>
      {showSmartMatchDialog && componentToBeMatched?.length > 0 && (
        <AISuggestionDialogContainer
          open={openAISuggestion}
          onClose={() => setOpenAISuggestion(false)}
          entity={selectedEntity}
          onLinkRequest={handleOpenNetworkInterface}
          onUnlinkRequest={handleSWModuleUnLink}
        />
      )}

      {openSWMLinkRecreateDialog && (
        <RecreateLinkDialog
          open={openSWMLinkRecreateDialog}
          onClose={closeRecreateDialog}
          entity={"EE"}
          linkList={recreateSWMLinkList}
          handleLink={handleOnClickRecreateLink}
        />
      )}
      <ModuleViewMoreDialog
        isEEDialogOpen={isSWMDialogOpen}
        toggleEEDialog={toggleSWMDialog}
        selectedComponent={selectedComponent}
        linkApiProps={linkApiProps}
        apiAction={apiAction}
        apiDataKey={apiDataKey}
        type={moduleViewMoreType}
      />
    </Box>
  );
}

export default SWMLinkPage;
